import { LoadingState } from '@bn-apps/dim-ui-react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import routes from '@config/routes';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredAccessLevel?: 'anonymous' | 'authenticated' | 'projectMember';
}

const ProtectedRoute = ({ children, requiredAccessLevel = 'authenticated' }: ProtectedRouteProps): JSX.Element => {
  const { t } = useTranslation();
  const { user, loading, login } = useAuth();

  // Check access level
  const accessLevelHierarchy: Record<string, number> = {
    anonymous: 0,
    authenticated: 1,
    projectMember: 2,
  };

  const userLevel = accessLevelHierarchy[user?.accessLevel ?? 'anonymous'] || 0;
  const requiredLevel = accessLevelHierarchy[requiredAccessLevel] || 0;
  const isAuthenticated = userLevel >= accessLevelHierarchy.authenticated;
  const needsLogin = !loading && userLevel < requiredLevel && !isAuthenticated;

  useEffect(() => {
    if (needsLogin) {
      // authService.login() reads window.location.href at call time, so
      // triggering it here (before any client-side navigation away from this
      // route) sends the user back to this page after they sign in.
      login();
    }
  }, [needsLogin, login]);

  if (loading || needsLogin) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '50vh' }}>
        <LoadingState label={t('common.loading')} />
      </div>
    );
  }

  if (userLevel < requiredLevel) {
    // Redirect authenticated users without sufficient privileges to an access-denied page if available
    const accessDeniedRoute =
      routes.accessDenied ?? routes.forbidden ?? routes.login;

    return <Navigate to={accessDeniedRoute} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
